import "./Input.css";
import classNames from "classnames";
import { forwardRef, useId, InputHTMLAttributes, ReactNode } from "react";
import { ReactComponent as ErrorIcon } from "@icons/error.svg";
import { ReactComponent as InfoIcon } from "@icons/info.svg";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode;
  label?: string;
  helperText?: string;
  state?: "error" | "info";
  className?: string;
  suffix?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({ icon, className, label, helperText, state, id, suffix, ...props }, ref) => {
  const generatedId = useId();
  const finalId = id ?? generatedId;

  const stateClasses = state === "error" ? "outline outline-2 outline-brand-red focus:outline-brand-red" : "";

  let MessageIcon;
  switch (state) {
    case "error":
      MessageIcon = ErrorIcon;
      break;
    case "info":
      MessageIcon = InfoIcon;
      break;
    default:
      MessageIcon = null;
  }

  return (
    <div className="relative space-y-1 w-full">
      {label && (
        <label htmlFor={finalId} className="font-bold text-navy-blue text-base">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={ref}
          id={finalId}
          aria-describedby={`aria-${finalId}`}
          className={classNames(
            "wisdom-input appearance-none h-[50px] w-full bg-l-blue text-navy-blue font-bold",
            "placeholder:font-bold placeholder:text-navy-blue-bright",
            "focus:outline-brand-purple focus:ring-0 rounded-[6px] px-4",
            suffix && "pr-10",
            stateClasses,
            className
          )}
          type="text"
          {...props}
        />

        {icon && <div className="absolute top-1/2 -translate-y-1/2 right-4 h-6 w-6 flex items-center justify-center">{icon}</div>}

        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-m-grey pointer-events-none">{suffix}</span>}
      </div>

      {helperText && (
        <div className="flex items-center mt-2">
          {MessageIcon && <MessageIcon width={16} height={18} />}
          <p id={`aria-${finalId}`} className="pl-1 text-sm">
            {helperText}
          </p>
        </div>
      )}
    </div>
  );
});
